import EVT, { assetSensorEventValueLabel, } from '@opsbase/lib-asset/AssetSensor/AssetSensorEventValueType';
import getUnitForSensorEventValueType from '@opsbase/lib-asset/helpers/getUnitForSensorEventValueType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import Palette from '@opsbase/lib-palette';
import useOrgIoTSensorEvents from '@opsbase/react-gql-hooks/organisation/useOrgIoTSensorEvents';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import useDimensions from '@opsbase/react-hooks/useDimensions';
import Line3DGrouped from '@opsbase/web-charts/Line3DGrouped';
import useCss from '@opsbase/web-css';
import Card from '@opsbase/web-layout/Card';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import DateTimePicker from '@opsbase/web-pickers/DatePicker/DateTimePicker';
import ActionButton from '@opsbase/web-ui/ActionButton';
import ActionButtonContainer from '@opsbase/web-ui/ActionButtonContainer';
import Spinner from '@opsbase/web-ui/Spinner';
import Text from '@opsbase/web-ui/Text';
import range from 'lodash/range';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
function initSensorCharts() {
    return {
        [EVT.TEMPERATURE]: [],
        [EVT.HUMIDITY]: [],
        [EVT.PROXIMITY_DETECTION]: [],
        [EVT.WATER_DETECTION]: [],
    };
}
const makeMyClasses = () => ({
    chartTitle: {
        textAlign: 'center',
        padding: 4,
    },
});
export function SensorCharts(props) {
    const { sensors } = props;
    const [loading, setLoading] = useState(true);
    const [lastLoaded, setLastLoaded] = useState(new Date());
    const [endTime, setEndTime] = useState(moment());
    const [noOfHours, setNoOfHours] = useState(12);
    const [startTime, setStartTime] = useState(endTime.clone().subtract(noOfHours, 'hours'));
    const [paneRef, paneDims] = useDimensions();
    const myCss = useCss(makeMyClasses);
    const chartData = useRef(initSensorCharts());
    const xLabels = useRef([]);
    const chartWidth = ((paneDims === null || paneDims === void 0 ? void 0 : paneDims.width) || 400) - 20;
    const { data: sensorEvents } = useOrgIoTSensorEvents({
        variables: {
            sensors,
            from: startTime,
            to: endTime,
        },
    });
    const loadData = useMemo(() => async () => {
        if (!(sensors === null || sensors === void 0 ? void 0 : sensors.length) || !(sensorEvents === null || sensorEvents === void 0 ? void 0 : sensorEvents.length))
            return;
        setLoading(true);
        const labelStep = calcLabelStep(noOfHours);
        xLabels.current = range(noOfHours / labelStep + 2).map(i => endTime
            .clone()
            .startOf('hour')
            .add(labelStep, 'hour')
            .subtract(i * labelStep, 'hour')
            .toDate()
            .getTime());
        await Promise.all(sensors.map(async (sensor, idx) => {
            const tempData = processChartData(calcColor(sensors, idx, 0), sensorEvents[idx], EVT.TEMPERATURE);
            const humidData = processChartData(calcColor(sensors, idx, 1), sensorEvents[idx], EVT.HUMIDITY);
            const proxData = processChartData(calcColor(sensors, idx, 2), sensorEvents[idx], EVT.PROXIMITY_DETECTION);
            const waterData = processChartData(calcColor(sensors, idx, 3), sensorEvents[idx], EVT.WATER_DETECTION);
            if (!!(tempData === null || tempData === void 0 ? void 0 : tempData.length))
                chartData.current[EVT.TEMPERATURE].push(tempData);
            if (!!(humidData === null || humidData === void 0 ? void 0 : humidData.length))
                chartData.current[EVT.HUMIDITY].push(humidData);
            if (!!(proxData === null || proxData === void 0 ? void 0 : proxData.length))
                chartData.current[EVT.PROXIMITY_DETECTION].push(proxData);
            if (!!(waterData === null || waterData === void 0 ? void 0 : waterData.length))
                chartData.current[EVT.WATER_DETECTION].push(waterData);
        }));
        setTimeout(() => {
            setLastLoaded(new Date());
            setLoading(false);
        }, 1000);
    }, [noOfHours, endTime, sensors, sensorEvents]);
    useEffect(() => {
        loadData().catch(doNothing);
    }, [loadData, noOfHours]);
    const tickFormat = useMemo(() => xValue => {
        const labelStep = calcLabelStep(noOfHours);
        const x = moment(xValue);
        const dateBit = x.format('DD MMM');
        const timeBit = labelStep < 1 ? x.format('h:mm A') : x.format('h a');
        return timeBit + '\n' + dateBit;
    }, [noOfHours]);
    const handleChangeHours = useCallforward(n => () => {
        setNoOfHours(n);
        setStartTime(endTime.clone().subtract(n, 'hours'));
    }, [endTime]);
    const handleChangeEndTime = useCallback(d => setEndTime(moment(d)), []);
    const renderChart = useMemo(() => (evt) => (React.createElement(Card, { ref: paneRef },
        React.createElement(Text, { className: myCss.chartTitle }, assetSensorEventValueLabel(evt)),
        React.createElement(Line3DGrouped, { dotSize: noOfHours > 24 ? 2 : 4, xLabels: xLabels.current, ySuffix: getUnitForSensorEventValueType(evt), tickFormat: tickFormat, data: chartData.current[evt], width: chartWidth, height: 200 }))), [chartWidth, myCss, noOfHours, paneRef, tickFormat]);
    const content = useMemo(() => () => {
        var _a, _b, _c, _d;
        return (React.createElement(Vertical, { gutter: true },
            !!((_a = chartData.current[EVT.TEMPERATURE]) === null || _a === void 0 ? void 0 : _a.length) && renderChart(EVT.TEMPERATURE),
            !!((_b = chartData.current[EVT.HUMIDITY]) === null || _b === void 0 ? void 0 : _b.length) && renderChart(EVT.HUMIDITY),
            !!((_c = chartData.current[EVT.PROXIMITY_DETECTION]) === null || _c === void 0 ? void 0 : _c.length) &&
                renderChart(EVT.PROXIMITY_DETECTION),
            !!((_d = chartData.current[EVT.WATER_DETECTION]) === null || _d === void 0 ? void 0 : _d.length) &&
                renderChart(EVT.WATER_DETECTION)));
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderChart, lastLoaded]);
    return useMemo(() => (React.createElement(Vertical, null,
        React.createElement(Horizontal, { centered: true, shrink: true, gutter: true },
            React.createElement(ActionButtonContainer, { justify: "flex-start" }, [6, 12, 24, 24 * 2, 24 * 7].map(i => (React.createElement(ActionButton, { highlight: noOfHours === i, label: i >= 24 ? i / 24 + 'd' : i + 'hr', onClick: handleChangeHours(i) })))),
            React.createElement(Pane, null,
                React.createElement(DateTimePicker, { white: true, value: endTime.toDate(), onChange: handleChangeEndTime }))),
        React.createElement(Spinner, { busy: loading }, content))), [content, endTime, handleChangeEndTime, handleChangeHours, loading, noOfHours]);
}
export default SensorCharts;
const chartColors = [
    Palette.GoodOldBlue.main,
    Palette.Greeny.main,
    Palette.CoralRed.main,
    Palette.OJ.main,
    Palette.HelixNotificationBold.main,
];
function calcColor(sensors, sensorIdx, chartIdx) {
    return chartColors[sensors.length > 1 ? sensorIdx : chartIdx];
}
function processChartData(color, sensorEvents, evt) {
    const unit = getUnitForSensorEventValueType(evt);
    return sensorEvents[evt].map(e => {
        const x = moment(e.timestamp);
        const y = e.value;
        return {
            label: `${y}${unit} @ ${x.format('h:mma')} on ${x.format('DD MMM')}`,
            color1: color,
            color2: color,
            x: x.toDate().getTime(),
            y: y,
            yRaw: y,
        };
    });
}
function calcLabelStep(noOfHours) {
    switch (noOfHours) {
        case 6:
            return 0.5;
        case 12:
            return 1;
        case 24:
            return 2;
        case 24 * 2:
            return 6;
        case 24 * 7:
            return 12;
    }
}
